import { makeStyles } from '@fluentui/react-components';
import { memo, useCallback, useMemo } from 'react';

import type { DayCell } from '../calendar';
import { sameEmail } from '../common';
import { useCurrentUser } from '../current-user';
import { palette, tokens } from '../theme';
import { workItemTypeAccent } from '../work-items';
import { type PendingEntry, usePending } from '../worklog';
import { EntryCard } from './EntryCard';

export type TodaysEntriesSectionProps = {
    cell: DayCell;
    // Hours still free on the day; a pending entry may grow into them.
    remaining: number;
};

type SavedEntry = DayCell['savedEntries'][number];
type AddFn = ReturnType<typeof usePending>['add'];
type RemoveFn = ReturnType<typeof usePending>['remove'];
type UpdateHoursFn = ReturnType<typeof usePending>['updateHours'];

export const TodaysEntriesSection: React.FC<TodaysEntriesSectionProps> = ({ cell, remaining }) => {
    const classes = useStyles();
    const { forDate, remove, add, updateHours } = usePending();
    const { actor } = useCurrentUser();
    const actorEmail = actor?.email ?? '';
    const pendingForDay = forDate(cell.date);

    const removalByKey = useMemo(
        () => new Map(pendingForDay.filter((p) => p.action === 'remove').map((p) => [`${p.workItemId}|${p.hours}`, p.id] as const)),
        [pendingForDay],
    );
    const adds = useMemo(() => pendingForDay.filter((p) => p.action === 'add'), [pendingForDay]);

    const isEmpty = cell.savedEntries.length === 0 && adds.length === 0;

    return (
        <div className={classes.root}>
            {isEmpty ? (
                <p className={classes.emptyText}>No entries yet — search a work item to add one.</p>
            ) : (
                <>
                    {cell.savedEntries.length > 0 && (
                        <section className={classes.section}>
                            <SubHeader title="Logged" count={cell.savedEntries.length} />
                            {cell.savedEntries.map((row, idx) => (
                                <LoggedRow
                                    key={`logged-${row.issueId}-${idx}`}
                                    row={row}
                                    pendingRemoveId={removalByKey.get(`${row.issueId}|${row.hours}`) ?? null}
                                    cellDate={cell.date}
                                    editable={sameEmail(row.authorEmail, actorEmail)}
                                    add={add}
                                    remove={remove}
                                />
                            ))}
                        </section>
                    )}
                    {adds.length > 0 && (
                        <section className={classes.pendingSection}>
                            <SubHeader title="Pending" count={adds.length} accent="pending" />
                            {adds.map((entry) => (
                                <PendingRow
                                    key={entry.id}
                                    entry={entry}
                                    maxHours={remaining + entry.hours}
                                    remove={remove}
                                    updateHours={updateHours}
                                />
                            ))}
                        </section>
                    )}
                </>
            )}
        </div>
    );
};

type LoggedRowProps = {
    row: SavedEntry;
    pendingRemoveId: string | null;
    cellDate: string;
    // ADO lets only the comment's author change it.
    editable: boolean;
    add: AddFn;
    remove: RemoveFn;
};

const LoggedRow = memo(function LoggedRow({ row, pendingRemoveId, cellDate, editable, add, remove }: LoggedRowProps) {
    const onRemove = useCallback(() => {
        if (pendingRemoveId) {
            remove(pendingRemoveId);
            return;
        }
        add({
            workItemId: row.issueId,
            workItemTitle: row.issueTitle,
            workItemType: row.workItemType,
            date: cellDate,
            hours: row.hours,
            action: 'remove',
        });
    }, [pendingRemoveId, row.issueId, row.issueTitle, row.workItemType, row.hours, cellDate, add, remove]);

    return (
        <EntryCard
            workItemId={row.issueId}
            title={row.issueTitle}
            hours={row.hours}
            accent={workItemTypeAccent(row.workItemType, row.issueTitle)}
            typeLabel={row.workItemType}
            status={pendingRemoveId ? 'pendingRemove' : 'saved'}
            onRemove={editable ? onRemove : undefined}
            removeAriaLabel={
                editable
                    ? pendingRemoveId
                        ? 'Undo removal'
                        : 'Mark for removal'
                    : `Logged by ${row.authorEmail} — only they can change it`
            }
        />
    );
});

type PendingRowProps = {
    entry: PendingEntry;
    maxHours: number;
    remove: RemoveFn;
    updateHours: UpdateHoursFn;
};

const PendingRow = memo(function PendingRow({ entry, maxHours, remove, updateHours }: PendingRowProps) {
    const onRemove = useCallback(() => remove(entry.id), [remove, entry.id]);
    const onHoursChange = useCallback((hours: number) => updateHours(entry.id, hours), [updateHours, entry.id]);

    return (
        <EntryCard
            workItemId={entry.workItemId}
            title={entry.workItemTitle}
            hours={entry.hours}
            accent={workItemTypeAccent(entry.workItemType, entry.workItemTitle)}
            typeLabel={entry.workItemType}
            status="pendingAdd"
            onRemove={onRemove}
            removeAriaLabel="Discard pending entry"
            onHoursChange={onHoursChange}
            maxHours={maxHours}
        />
    );
});

type SubHeaderProps = { title: string; count: number; accent?: 'pending' };

const SubHeader: React.FC<SubHeaderProps> = ({ title, count, accent }) => {
    const classes = useStyles();
    return (
        <div className={classes.subHeader}>
            <span className={accent === 'pending' ? classes.subHeaderTitlePending : classes.subHeaderTitle}>{title}</span>
            <span className={classes.subHeaderDot} aria-hidden>
                ·
            </span>
            <span className={accent === 'pending' ? classes.subHeaderCountPending : classes.subHeaderCount}>{count}</span>
        </div>
    );
};

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        paddingRight: tokens.spacingHorizontalS,
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
    },
    pendingSection: {
        display: 'flex',
        flexDirection: 'column',
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalXS}`,
        backgroundColor: palette.pendingCardBg,
        border: `1px solid ${palette.pendingPillBg}`,
        borderRadius: tokens.borderRadiusMedium,
    },
    emptyText: {
        margin: 0,
        padding: `${tokens.spacingVerticalL} 0`,
        fontSize: '13px',
        color: palette.dimText,
        textAlign: 'center',
    },
    subHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 0 8px',
    },
    subHeaderTitle: {
        fontSize: '11px',
        fontWeight: tokens.fontWeightSemibold,
        color: palette.dimText,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    subHeaderTitlePending: {
        fontSize: '11px',
        fontWeight: tokens.fontWeightSemibold,
        color: palette.pendingPillText,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    subHeaderDot: {
        fontSize: '11px',
        color: palette.dimText,
        lineHeight: 1,
    },
    subHeaderCount: {
        fontSize: '11px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground3,
    },
    subHeaderCountPending: {
        fontSize: '11px',
        fontWeight: tokens.fontWeightSemibold,
        color: palette.pendingPillText,
    },
});
